import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

const ObservationAndRemarks = () => {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentObservation, setCurrentObservation] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalObservations, setTotalObservations] = useState(0);

  // Dynamic form rows
  const [formRows, setFormRows] = useState([
    { id: 1, observation_text: '', remark_option: '' }
  ]);

  // Remark options
  const remarkOptions = [
    'Repair',
    'Acceptable',
    'Reshoot',
    'DRS',
    'Fill up and DRS',
    'Retake',
    'Fill up',
    'CRS',
    'Check Surface',
    'L I',
    'L II',
    'L III',
    'L IV',
    'L V',
    'L VI',
    'L VII',
    'L VIII',
    'Not Acceptable',
    'Reshoot to Confirm'
  ];

  // Fetch observations
  const fetchObservations = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/observations?page=${page}&limit=10&search=${searchTerm}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setObservations(response.data.data);
        setCurrentPage(response.data.pagination.page);
        setTotalPages(response.data.pagination.totalPages);
        setTotalObservations(response.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to fetch observations');
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObservations(currentPage, search);
  }, []);

  // Add new row
  const handleAddRow = () => {
    const newId = formRows.length > 0 ? Math.max(...formRows.map(r => r.id)) + 1 : 1;
    setFormRows([...formRows, { id: newId, observation_text: '', remark_option: '' }]);
  };

  // Remove row
  const handleRemoveRow = (id) => {
    if (formRows.length === 1) {
      setError('At least one row is required');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setFormRows(formRows.filter(row => row.id !== id));
  };

  // Handle row change
  const handleRowChange = (id, field, value) => {
    setFormRows(formRows.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  // Handle form submit (Bulk Create or Single Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = Cookies.get('token');

      if (editMode) {
        // Update single observation
        const row = formRows[0];
        const response = await axios.put(
          `https://app-pune-ndt.cloud/api/observations/${currentObservation.id}`,
          {
            observation_text: row.observation_text,
            remark_option: row.remark_option
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          setSuccess('Observation updated successfully!');
          resetForm();
          fetchObservations(currentPage, search);
          setTimeout(() => setSuccess(''), 3000);
        }
      } else {
        // Bulk create observations
        const validRows = formRows.filter(
          row => row.observation_text.trim() !== '' && row.remark_option !== ''
        );

        if (validRows.length === 0) {
          setError('Please fill at least one row completely');
          setLoading(false);
          return;
        }

        const response = await axios.post(
          'https://app-pune-ndt.cloud/api/observations/bulk',
          { observations: validRows },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success) {
          setSuccess(`${validRows.length} observation(s) created successfully!`);
          resetForm();
          fetchObservations(currentPage, search);
          setTimeout(() => setSuccess(''), 3000);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to save observations';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = async (observation) => {
    setCurrentObservation(observation);
    setEditMode(true);
    setShowForm(true);

    setFormRows([{
      id: 1,
      observation_text: observation.observation_text,
      remark_option: observation.remark_option
    }]);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this observation?')) {
      return;
    }

    try {
      const token = Cookies.get('token');
      const response = await axios.delete(
        `https://app-pune-ndt.cloud/api/observations/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccess('Observation deleted successfully!');
        fetchObservations(currentPage, search);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete observation';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormRows([{ id: 1, observation_text: '', remark_option: '' }]);
    setShowForm(false);
    setEditMode(false);
    setCurrentObservation(null);
    setError('');
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1);
    fetchObservations(1, value);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchObservations(page, search);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Observations & Remarks</h1>
              <p className="text-gray-600 mt-1">Total Entries: {totalObservations}</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="bg-gradient-to-r from-indigo-900 to-indigo-900 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition duration-300 shadow-lg"
            >
              {showForm ? '✕ Cancel' : '+ Add Observations'}
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Dynamic Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editMode ? 'Edit Observation' : 'Add New Observations'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {formRows.map((row, index) => (
                <div key={row.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start space-x-4">
                    {/* Sr. No */}
                    <div className="flex-shrink-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sr. No
                      </label>
                      <div className="w-16 px-4 py-2 bg-indigo-100 text-cyan-900 rounded-lg text-center font-bold">
                        {index + 1}
                      </div>
                    </div>

                    {/* Observation Text */}
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Observation *
                      </label>
                      <input
                        type="text"
                        value={row.observation_text}
                        onChange={(e) => handleRowChange(row.id, 'observation_text', e.target.value)}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        placeholder="Enter observation..."
                      />
                    </div>

                    {/* Remark Option */}
                    <div className="w-64">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Remark *
                      </label>
                      <select
                        value={row.remark_option}
                        onChange={(e) => handleRowChange(row.id, 'remark_option', e.target.value)}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      >
                        <option value="">Select Remark</option>
                        {remarkOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Action Buttons */}
                    {!editMode && (
                      <div className="flex-shrink-0 pt-7 flex space-x-2">
                        {/* Remove Button - Only show if more than 1 row */}
                        {formRows.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(row.id)}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-300"
                            title="Remove Row"
                          >
                            −
                          </button>
                        )}

                        {/* Add Button - Always visible in create mode */}
                        <button
                          type="button"
                          onClick={handleAddRow}
                          className="px-3 py-2 bg-indigo-900 text-white rounded-lg hover:bg-blue-700 transition duration-300"
                          title="Add Row"
                        >
                          +
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-900 to-indigo-900 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition duration-300 disabled:opacity-50 shadow-lg"
                >
                  {loading ? 'Saving...' : editMode ? 'Update Observation' : '💾 Save All Observations'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition duration-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
          <input
            type="text"
            placeholder="Search observations or remarks..."
            value={search}
            onChange={handleSearch}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>

        {/* Observations Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-indigo-900 to-indigo-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Observation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Remark
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : observations.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                      No observations found
                    </td>
                  </tr>
                ) : (
                  observations.map((observation) => (
                    <tr key={observation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {observation.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                        {observation.observation_text}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="bg-cyan-50 text-cyan-800 px-3 py-1 rounded-full font-semibold">
                          {observation.remark_option}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(observation.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(observation)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(observation.id)}
                          className="text-red-600 hover:text-red-900 font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === index + 1
                            ? 'z-10 bg-cyan-600 border-cyan-600 text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObservationAndRemarks;